import { defineAsyncComponent, defineComponent } from 'vue';
import * as constants from '@/common/constants';
import raw_settings from '@/genfiles/settings.json';
const AdminHome = defineAsyncComponent(() => import(/* webpackChunkName: "admin" */ '@/client/components/admin/AdminHome.vue'));
const CardList = defineAsyncComponent(() => import(/* webpackChunkName: "card-list" */ '@/client/components/cardlist/CardList.vue'));
const CreateGameForm = defineAsyncComponent(() => import(/* webpackChunkName: "create-game" */ '@/client/components/create/CreateGameForm.vue'));
const GameEnd = defineAsyncComponent(() => import(/* webpackChunkName: "game-end" */ '@/client/components/GameEnd.vue'));
const GameHome = defineAsyncComponent(() => import(/* webpackChunkName: "game-home" */ '@/client/components/GameHome.vue'));
const GamesOverview = defineAsyncComponent(() => import(/* webpackChunkName: "games-overview" */ '@/client/components/GamesOverview.vue'));
const Help = defineAsyncComponent(() => import(/* webpackChunkName: "help" */ '@/client/components/help/Help.vue'));
const LoginHome = defineAsyncComponent(() => import(/* webpackChunkName: "login" */ '@/client/components/auth/LoginHome.vue'));
const LoadGameForm = defineAsyncComponent(() => import(/* webpackChunkName: "load-game" */ '@/client/components/LoadGameForm.vue'));
const PlayerHome = defineAsyncComponent(() => import(/* webpackChunkName: "player-home" */ '@/client/components/PlayerHome.vue'));
const SpectatorHome = defineAsyncComponent(() => import(/* webpackChunkName: "spectator-home" */ '@/client/components/SpectatorHome.vue'));
const StartScreen = defineAsyncComponent(() => import(/* webpackChunkName: "start-screen" */ '@/client/components/StartScreen.vue'));
import { $t, setTranslationContext } from '@/client/directives/i18n';
import { paths } from '@/common/app/paths';
import { isPlayerId, isSpectatorId } from '@/common/Types';
import { hasShowModal, showModal, windowHasHTMLDialogElement } from './HTMLDialogElementCompatibility';
import dialogPolyfill from 'dialog-polyfill';
// NOTE: this simplistic truncation to the last segment might cause issues if
// this page starts supporting paths more than one level deep.
function getLastPathSegment() {
    // Leave only the last part of /path
    return window.location.pathname.replace(/.*\//g, '');
}
export default defineComponent({
    name: 'App',
    data() {
        return {
            screen: 'empty',
            playerkey: 0,
            settings: raw_settings,
            isServerSideRequestInProgress: false,
            componentsVisibility: {
                'milestones': true,
                'awards_list': true,
                'tags_concise': false,
                'pinned_player_0': false,
                'pinned_player_1': false,
                'pinned_player_2': false,
                'pinned_player_3': false,
                'pinned_player_4': false,
                'turmoil_parties': false,
            },
            game: undefined,
            playerView: undefined,
            spectator: undefined,
            login: undefined,
        };
    },
    components: {
        // These component keys match the screen values, and their entries in index.html.
        'start-screen': StartScreen,
        'create-game-form': CreateGameForm,
        'load-game-form': LoadGameForm,
        'game-home': GameHome,
        'player-home': PlayerHome,
        'spectator-home': SpectatorHome,
        'game-end': GameEnd,
        'games-overview': GamesOverview,
        'card-list': CardList,
        'help': Help,
        'admin-home': AdminHome,
        'login-home': LoginHome,
    },
    methods: {
        showAlert(title, message, cb = () => { }) {
            const dialogElement = document.getElementById('alert-dialog');
            const buttonElement = document.getElementById('alert-dialog-button');
            const messageElement = document.getElementById('alert-dialog-message');
            const titleElement = document.getElementById('alert-dialog-title');
            if (buttonElement !== null && titleElement !== null && messageElement !== null && dialogElement !== null && hasShowModal(dialogElement)) {
                messageElement.innerHTML = $t(message);
                titleElement.textContent = $t(title);
                const handler = () => {
                    buttonElement.removeEventListener('click', handler);
                    cb();
                };
                buttonElement.addEventListener('click', handler);
                showModal(dialogElement);
            }
            else {
                alert(message);
                cb();
            }
        },
        setVisibilityState(targetVar, isVisible) {
            if (isVisible === this.getVisibilityState(targetVar))
                return;
            this.componentsVisibility[targetVar] = isVisible;
        },
        getVisibilityState(targetVar) {
            return this.componentsVisibility[targetVar] ? true : false;
        },
        update(path) {
            const currentPathname = getLastPathSegment();
            const app = this;
            const url = 'api/' + path + window.location.search.replace('&noredirect', '');
            fetch(url)
                .then((resp) => {
                if (!resp.ok) {
                    throw new Error(`Error getting game data: ${resp.statusText}`);
                }
                return resp.json();
            })
                .then((model) => {
                if (path === paths.PLAYER) {
                    app.playerView = model;
                    setTranslationContext(app.playerView);
                }
                else if (path === paths.SPECTATOR) {
                    app.spectator = model;
                }
                app.playerkey++;
                if (model.game.phase === 'end' &&
                    window.location.search.includes('&noredirect') === false) {
                    app.screen = 'the-end';
                    if (currentPathname !== paths.THE_END) {
                        window.history.replaceState(model, `${constants.APP_NAME} - Player`, `${paths.THE_END}?id=${model.id}`);
                    }
                }
                else {
                    if (path === paths.PLAYER) {
                        app.screen = 'player-home';
                    }
                    else if (path === paths.SPECTATOR) {
                        app.screen = 'spectator-home';
                    }
                    if (currentPathname !== path) {
                        window.history.replaceState(model, `${constants.APP_NAME} - Game`, `${path}?id=${model.id}`);
                    }
                }
            })
                .catch((err) => {
                alert('Error getting game data');
                console.error(err);
            });
        },
        updatePlayer() {
            this.update(paths.PLAYER);
        },
        updateSpectator() {
            this.update(paths.SPECTATOR);
        },
    },
    mounted() {
        document.title = constants.APP_NAME;
        if (!windowHasHTMLDialogElement()) {
            dialogPolyfill.registerDialog(document.getElementById('alert-dialog'));
        }
        const currentPathname = getLastPathSegment();
        const app = this;
        if (currentPathname === paths.PLAYER) {
            app.updatePlayer();
        }
        else if (currentPathname === paths.THE_END) {
            const urlParams = new URLSearchParams(window.location.search);
            const id = urlParams.get('id') || '';
            if (isPlayerId(id)) {
                app.updatePlayer();
            }
            else if (isSpectatorId(id)) {
                app.updateSpectator();
            }
            else {
                alert('Bad id URL parameter.');
            }
        }
        else if (currentPathname === paths.GAME) {
            const url = paths.API_GAME + window.location.search;
            fetch(url)
                .then((resp) => {
                if (!resp.ok) {
                    throw new Error(`Error getting game data: ${resp.statusText}`);
                }
                return resp.json();
            })
                .then((appGame) => {
                app.screen = 'game-home';
                app.game = appGame;
                window.history.replaceState(appGame, `${constants.APP_NAME} - Game`, `${paths.GAME}?id=${appGame.id}`);
            })
                .catch((err) => {
                alert('Error getting game data');
                console.error(err);
            });
        }
        else if (currentPathname === paths.GAMES_OVERVIEW) {
            app.screen = 'games-overview';
        }
        else if (currentPathname === paths.NEW_GAME) {
            app.screen = 'create-game-form';
        }
        else if (currentPathname === paths.LOAD) {
            app.screen = 'load';
        }
        else if (currentPathname === paths.CARDS) {
            app.screen = 'cards';
        }
        else if (currentPathname === paths.HELP) {
            app.screen = 'help';
        }
        else if (currentPathname === paths.SPECTATOR) {
            app.updateSpectator();
        }
        else if (currentPathname === paths.ADMIN) {
            app.screen = 'admin';
        }
        else if (currentPathname === paths.LOGIN) {
            app.screen = 'login-home';
        }
        else {
            app.screen = 'start-screen';
        }
    },
});
